import {FlowchartStaticEdgeData, StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {StaticFlowchartNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {PromoteTransitionContext} from '../PromoteTransitionModal';
import {getOptionalTransitionsForNewEdge, shouldAddEdges} from '../types/transition/transitionLifecycleUtils';
import {Transition} from '../types/transition/TransitionTypes';
import {trackOptionalTransitionPromotedAnalytics} from './useFlowchartStateAnalytics';

export interface ShouldAddEdgesForNewNodeParams {
  edges: Array<FlowchartStaticEdgeData>;
  newNodeStaticData: StaticFlowchartNodeData;
  staticFlowchart: StaticTemplateFlowchart;
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>;
  transitions: Record<string, Transition>;
  flowchartTypeName: string;
}

/**
 * Determines whether edges should be added for a new node being placed on the flowchart.
 *
 * Augments the current flowchart nodes with the new node (which isn't yet in state),
 * checks if the edges should be added (potentially showing a promotion modal for
 * optional transitions), and tracks analytics for any promoted transitions.
 */
export async function shouldAddEdgesForNewNode({
  edges,
  newNodeStaticData,
  staticFlowchart,
  ptdvPairToTransitionIdIndex,
  transitions,
  flowchartTypeName,
}: ShouldAddEdgesForNewNodeParams): Promise<boolean> {
  // The new node is not yet in staticFlowchart.nodesStaticData because this
  // function runs before applyFlowchartChanges adds it to state.
  const augmentedNodesStaticData = [...staticFlowchart.nodesStaticData, newNodeStaticData];
  const result = await shouldAddEdges({
    edges,
    staticFlowchart: {...staticFlowchart, nodesStaticData: augmentedNodesStaticData},
    ptdvPairToTransitionIdIndex,
    transitions,
    context: PromoteTransitionContext.SELECT_TASK_SCHEMA,
  });
  if (result) {
    const allEdges = [...staticFlowchart.edgesStaticData, ...edges];
    const allOptionalTransitions = edges.flatMap((edge) =>
      getOptionalTransitionsForNewEdge(edge, augmentedNodesStaticData, allEdges, transitions)
    );
    trackOptionalTransitionPromotedAnalytics(allOptionalTransitions, flowchartTypeName);
  }
  return result;
}
